import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ResizableColumnControllerContext,
    ResizableColumnRegistration,
    ResizeStrategy,
    ToggleMeasureBranch,
} from "../../state/resizable-column-controller.context";
import {
    applyDragDelta,
    applyToggleAwareColumnWidth,
    enforceConstraints,
    preserveClosedColumnWidths as preserveClosedColumnWidthsLayout,
    resolveColumnWidths,
} from "./resizable-column-layout";

interface ResizableColumnControllerProps {
    resizeStrategy?: ResizeStrategy;
    keyboardStep?: number;
    keyboardFineStep?: number;
    children: React.ReactNode;
}

const SEPARATOR_WIDTH_PX = 8;

export function ResizableColumnController({
    resizeStrategy = "resize-neighbors",
    keyboardStep = 24,
    keyboardFineStep = 8,
    children,
}: ResizableColumnControllerProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const columnsRef = useRef<ResizableColumnRegistration[]>([]);
    const [columnsSnapshot, setColumnsSnapshot] = useState<ResizableColumnRegistration[]>([]);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [widths, setWidths] = useState<number[]>([]);
    const dragRef = useRef<{ index: number; startX: number; widths: number[] } | null>(null);
    const rafRef = useRef<number | null>(null);
    const widthsRef = useRef<number[]>([]);
    const manualWidthsRef = useRef<Record<string, number>>({});
    const toggleBranchRef = useRef<Record<string, ToggleMeasureBranch>>({});
    const lastContainerWidthUsedRef = useRef<number>(0);
    // Kept in sync with containerWidth state but readable inside stable callbacks
    // without making those callbacks depend on (and thus re-create on) every resize.
    const containerWidthRef = useRef<number>(0);

    const registrationsEqual = (left: ResizableColumnRegistration, right: ResizableColumnRegistration): boolean => {
        return left.id === right.id
            && left.width === right.width
            && left.minWidth === right.minWidth
            && left.maxWidth === right.maxWidth
            && left.autoSizeFromToggle === right.autoSizeFromToggle
            && left.showSeparator === right.showSeparator;
    };

    const getAvailableWidth = useCallback((columns: ResizableColumnRegistration[], totalWidth: number): number => {
        const safeWidth = Number.isFinite(totalWidth) ? Math.max(0, totalWidth) : 0;
        if (columns.length <= 1) {
            return safeWidth;
        }
        let separators = 0;
        for (let index = 0; index < columns.length - 1; index++) {
            if (columns[index].showSeparator !== false) {
                separators += 1;
            }
        }
        return Math.max(0, safeWidth - (separators * SEPARATOR_WIDTH_PX));
    }, []);

    const upsertColumn = useCallback((registration: ResizableColumnRegistration) => {
        const existing = columnsRef.current.findIndex((column) => column.id === registration.id);
        if (existing >= 0) {
            if (registrationsEqual(columnsRef.current[existing], registration)) {
                return;
            }
            columnsRef.current[existing] = registration;
        } else {
            columnsRef.current = [...columnsRef.current, registration];
        }
        setColumnsSnapshot([...columnsRef.current]);
    }, []);

    const registerColumn = useCallback((registration: ResizableColumnRegistration) => {
        upsertColumn(registration);
    }, [upsertColumn]);

    const updateColumn = useCallback((registration: ResizableColumnRegistration) => {
        upsertColumn(registration);
    }, [upsertColumn]);

    const unregisterColumn = useCallback((id: string) => {
        columnsRef.current = columnsRef.current.filter((column) => column.id !== id);
        delete manualWidthsRef.current[id];
        delete toggleBranchRef.current[id];
        setColumnsSnapshot([...columnsRef.current]);
    }, []);

    const buildClosedColumnIds = useCallback((): Set<string> => {
        const closed = new Set<string>();
        columnsRef.current.forEach((column) => {
            if (toggleBranchRef.current[column.id] === "fallback") {
                closed.add(column.id);
            }
        });
        return closed;
    }, []);

    // preserveClosedColumnWidths reads containerWidthRef so it is stable across
    // container-size changes.  A stable identity prevents reportMeasuredToggleWidth
    // from becoming a new function on every resize, which in turn stops ResizableColumn
    // from re-running its measurement effect (and emitting stale DOM measurements) every
    // time the container width changes.
    const preserveClosedColumnWidths = useCallback(
        (next: number[], previous: number[], triggerColumnId?: string): number[] => {
            const targetTotal = getAvailableWidth(columnsRef.current, containerWidthRef.current);
            return preserveClosedColumnWidthsLayout(
                next,
                previous,
                columnsRef.current,
                buildClosedColumnIds(),
                targetTotal,
                triggerColumnId,
            );
        },
        [buildClosedColumnIds, getAvailableWidth],
    );

    useEffect(() => {
        widthsRef.current = widths;
    }, [widths]);

    useEffect(() => {
        containerWidthRef.current = containerWidth;
    }, [containerWidth]);

    const reportMeasuredToggleWidth = useCallback((columnId: string, measuredWidthPx: number, branch: ToggleMeasureBranch) => {
        toggleBranchRef.current[columnId] = branch;
        setWidths((previous) => {
            const columnIndex = columnsRef.current.findIndex((column) => column.id === columnId);
            if (columnIndex < 0) {
                return previous;
            }
            const availableWidth = getAvailableWidth(columnsRef.current, containerWidthRef.current);
            const next = applyToggleAwareColumnWidth(
                previous,
                columnsRef.current,
                columnIndex,
                measuredWidthPx,
                branch,
                manualWidthsRef.current,
                availableWidth,
            );
            const exemptColumnId = branch === "controlled" ? columnId : undefined;
            return preserveClosedColumnWidths(next, previous, exemptColumnId);
        });
    }, [getAvailableWidth, preserveClosedColumnWidths]);

    useEffect(() => {
        if (typeof window === "undefined" || !containerRef.current) {
            return;
        }

        const element = containerRef.current;
        const measure = () => {
            const rect = element.getBoundingClientRect();
            setContainerWidth(rect.width || 0);
        };
        measure();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", measure);
            return () => window.removeEventListener("resize", measure);
        }

        const observer = new ResizeObserver(() => measure());
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const prevContainerWidth = lastContainerWidthUsedRef.current;
        lastContainerWidthUsedRef.current = containerWidth;

        setWidths((previous) => {
            if (columnsSnapshot.length === 0) {
                return [];
            }
            // Only treat `previous` as a valid base for the constraint path when the prior
            // layout was computed against a real (non-zero) container width.  Widths produced
            // while `containerWidth === 0` are minWidth-clamped artefacts of resolveColumnWidths
            // and must never seed enforceConstraints (#2413).
            if (
                prevContainerWidth > 0 &&
                previous.length === columnsSnapshot.length &&
                previous.some((value) => value > 0)
            ) {
                const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
                const constrained = enforceConstraints(previous, columnsSnapshot, availableWidth);
                return preserveClosedColumnWidths(constrained, previous);
            }
            const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
            const resolved = resolveColumnWidths(columnsSnapshot, availableWidth);
            return preserveClosedColumnWidths(resolved, previous);
        });
    }, [columnsSnapshot, containerWidth, getAvailableWidth, preserveClosedColumnWidths]);

    const getColumnWidth = useCallback((id: string): number => {
        const index = columnsSnapshot.findIndex((column) => column.id === id);
        return index >= 0 ? widths[index] || 0 : 0;
    }, [columnsSnapshot, widths]);

    const getColumnPercentage = useCallback((id: string): number => {
        const width = getColumnWidth(id);
        const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
        if (availableWidth <= 0) {
            return 0;
        }
        return Math.round((width / availableWidth) * 100);
    }, [columnsSnapshot, containerWidth, getAvailableWidth, getColumnWidth]);

    const applySeparatorDrag = useCallback((index: number, delta: number) => {
        setWidths((current) => {
            const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
            const next = applyDragDelta(
                current,
                columnsSnapshot,
                index,
                delta,
                resizeStrategy,
                availableWidth,
                buildClosedColumnIds(),
            );
            const triggerColumnId = columnsSnapshot[index]?.id;
            return preserveClosedColumnWidths(next, current, triggerColumnId);
        });
    }, [buildClosedColumnIds, columnsSnapshot, containerWidth, getAvailableWidth, preserveClosedColumnWidths, resizeStrategy]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const onMove = (event: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) {
                return;
            }
            const delta = event.clientX - drag.startX;
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = window.requestAnimationFrame(() => {
                setWidths((current) => {
                    const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
                    const next = applyDragDelta(
                        drag.widths,
                        columnsSnapshot,
                        drag.index,
                        delta,
                        resizeStrategy,
                        availableWidth,
                        buildClosedColumnIds(),
                    );
                    const triggerColumnId = columnsSnapshot[drag.index]?.id;
                    return preserveClosedColumnWidths(next, current, triggerColumnId);
                });
            });
        };

        const onUp = () => {
            const hadDrag = !!dragRef.current;
            dragRef.current = null;
            if (rafRef.current !== null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            if (hadDrag && typeof window !== "undefined") {
                window.requestAnimationFrame(() => {
                    columnsRef.current.forEach((column, index) => {
                        const width = widthsRef.current[index];
                        const isClosed = toggleBranchRef.current[column.id] === "fallback";
                        if (!isClosed && typeof width === "number" && Number.isFinite(width)) {
                            manualWidthsRef.current[column.id] = width;
                        }
                    });
                });
            }
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, [buildClosedColumnIds, columnsSnapshot, containerWidth, getAvailableWidth, preserveClosedColumnWidths, resizeStrategy]);

    const getSeparatorProps = useCallback((index: number) => {
        const controls = `${columnsSnapshot[index]?.id || ""} ${columnsSnapshot[index + 1]?.id || ""}`.trim();
        const availableWidth = getAvailableWidth(columnsSnapshot, containerWidth);
        const valueNow = Math.max(0, Math.min(100, Math.round((widths[index] || 0) / Math.max(1, availableWidth) * 100)));
        return {
            role: "separator" as const,
            tabIndex: 0,
            "aria-orientation": "vertical" as const,
            "aria-label": "Resize Panel",
            "aria-controls": controls,
            "aria-valuemin": 0,
            "aria-valuemax": 100,
            "aria-valuenow": valueNow,
            onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
                dragRef.current = { index, startX: event.clientX, widths: [...widths] };
            },
            onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                const step = event.ctrlKey ? keyboardFineStep : keyboardStep;
                if (event.key === "ArrowRight") {
                    event.preventDefault();
                    applySeparatorDrag(index, step);
                } else if (event.key === "ArrowLeft") {
                    event.preventDefault();
                    applySeparatorDrag(index, -step);
                } else if (event.key === "Home") {
                    event.preventDefault();
                    applySeparatorDrag(index, -100000);
                } else if (event.key === "End") {
                    event.preventDefault();
                    applySeparatorDrag(index, 100000);
                }
            },
        };
    }, [applySeparatorDrag, columnsSnapshot, containerWidth, getAvailableWidth, keyboardFineStep, keyboardStep, widths]);

    const contextValue = useMemo(() => ({
        registerColumn,
        unregisterColumn,
        updateColumn,
        reportMeasuredToggleWidth,
        getColumnWidth,
        getColumnPercentage,
        getSeparatorProps,
        getColumnIdByIndex: (index: number) => columnsSnapshot[index]?.id || null,
    }), [columnsSnapshot, getColumnPercentage, getColumnWidth, getSeparatorProps, registerColumn, reportMeasuredToggleWidth, unregisterColumn, updateColumn]);

    return (
        <ResizableColumnControllerContext.Provider value={contextValue}>
            <div
                ref={containerRef}
                style={{
                    display: "flex",
                    alignItems: "stretch",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                {children}
            </div>
        </ResizableColumnControllerContext.Provider>
    );
}
